
import { supabase, enhancedQuery } from "@/integrations/supabase/client";
import { Profile } from "@/types/auth";

type ProfileUpdateInput = {
  first_name?: string;
  last_name?: string;
  role?: 'admin' | 'organization' | 'public' | 'administrator' | 'organization_user';
  organization_id?: string | null;
  is_active?: boolean;
};

// Get all profiles with their organizations
export const getProfiles = async (): Promise<Profile[]> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        organization:organizations(*)
      `)
      .order('first_name');
    
    if (error) {
      console.error('Error fetching profiles:', error);
      throw error;
    }
    
    return data as unknown as Profile[];
  } catch (error) {
    console.error('Unexpected error in getProfiles:', error);
    throw error;
  }
};

// Get a single profile by ID
export const getProfileById = async (id: string): Promise<Profile> => {
  return enhancedQuery(async () => {
    const result = await supabase
      .from('profiles')
      .select(`
        *,
        organization:organizations(*)
      `)
      .eq('id', id)
      .single();
      
    return result;
  });
};

// Update an existing profile
export const updateProfile = async (id: string, input: ProfileUpdateInput): Promise<Profile> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...(input.first_name !== undefined && { first_name: input.first_name }),
        ...(input.last_name !== undefined && { last_name: input.last_name }),
        ...(input.role !== undefined && { role: input.role }),
        ...(input.organization_id !== undefined && { organization_id: input.organization_id }),
        ...(input.is_active !== undefined && { is_active: input.is_active }),
      })
      .eq('id', id)
      .select(`
        *,
        organization:organizations(*)
      `);
    
    if (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      throw new Error('Failed to update profile: Profile not found');
    }
    
    return data[0] as unknown as Profile;
  } catch (error) {
    console.error('Unexpected error in updateProfile:', error);
    throw error;
  }
};

// Delete a profile (deactivate user)
export const deactivateProfile = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ is_active: false })
      .eq('id', id);
    
    if (error) {
      console.error('Error deactivating profile:', error);
      throw error;
    }
  } catch (error) {
    console.error('Unexpected error in deactivateProfile:', error);
    throw error;
  }
};
